import { useState, useCallback, useEffect, useContext } from "react";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import type { DealFinderTicker } from "../api/tickers/models/DealFinderTicker";
import { SettingsProviderContext } from "screens/panels/settings/SettingsProvider";

export interface UseInvestmentDetailModalResult {
  isOpen: boolean;
  selectedDeal: DealFinderTicker | null;
  currentDealId: string | null;
  currentExchangeTicker: string;
  openModal: (deal: DealFinderTicker) => void;
  closeModal: () => void;
  setSelectedDeal: (deal: DealFinderTicker | null) => void;
}

/**
 * Custom hook to handle the investment detail modal state via URL and local state
 * This allows for sharing URLs that open specific deals while maintaining compatibility
 * with the existing implementation
 */
export const useInvestmentDetailModal = (): UseInvestmentDetailModalResult => {
  const [selectedDeal, setSelectedDeal] = useState<DealFinderTicker | null>(null);
  const { setActiveStep } = useContext(SettingsProviderContext);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { symbolWithExchange, dealId } = useParams<{ symbolWithExchange?: string; dealId?: string }>();

  // Get the current deal ID from the URL params or search params
  // For the new route format, we don't have a dealId directly, but we can use symbolWithExchange presence
  const currentDealId = dealId ?? searchParams.get("dealId") ?? null;
  const currentExchangeTicker = symbolWithExchange?.toLowerCase() || "";
  const hasExchangeAndTicker = !!symbolWithExchange;

  // Determine if the modal should be open based on the presence of dealId in the URL or search params,
  // or the presence of exchange and ticker parameters
  const isOpen = (!!currentDealId && (!!dealId || searchParams.get("dealId") === currentDealId)) || hasExchangeAndTicker;

  // Effect to handle state consistency with URL changes (e.g., back button)
  useEffect(() => {
    if (!isOpen && selectedDeal) {
      // Optional: Clear selected deal when modal closes via URL change
      // Kept commented for smoother transition if closed via button
      // setSelectedDeal(null);
    }
  }, [isOpen, selectedDeal]);

  // Open a deal modal by adding the exchange:ticker to the URL and updating local state
  const openModal = useCallback(
    (deal: DealFinderTicker) => {
      setActiveStep(-2); // Reset active step to -2 when opening a deal modal
      setSelectedDeal(deal);
      const symbolWithExchangeParam = `${deal.exchange}:${deal.symbol}`;
      if (location.pathname.includes("/deal-finder")) {
        // If we're already in the deal finder section, use the deal finder path
        navigate(`/deal-finder/${symbolWithExchangeParam}`);
      } else {
        // append /{exchange:symbol} to the current route
        const newPath = `${location.pathname}${location.pathname.endsWith("/") ? "" : "/"}${symbolWithExchangeParam}`;
        navigate(newPath, { replace: true });
      }
    },
    [setActiveStep, location.pathname, navigate]
  );

  // Close the deal modal by navigating back to the base path and updating local state
  const closeModal = useCallback(() => {
    // Navigate back to the base path
    if (location.pathname.includes("/deal-finder")) {
      // Only navigate to /deal-finder if we're already in the deal finder section
      navigate("/deal-finder");
    } else if (searchParams.has("dealId")) {
      // If we're using search params, remove the dealId param
      searchParams.delete("dealId");
      setSearchParams(searchParams);
    } else if (symbolWithExchange) {
      // If we have a symbolWithExchange parameter, navigate back to the base path without it
      const pathSegments = location.pathname.split("/");
      const lastSegment = pathSegments[pathSegments.length - 1];

      // Check if the last segment matches the symbolWithExchange format (contains colon)
      if (lastSegment === symbolWithExchange || lastSegment.includes(":")) {
        // Remove the last segment (symbolWithExchange) from the path
        const basePath = pathSegments.slice(0, -1).join("/") || "/";
        navigate(basePath);
      }
    }
    // We'll keep the selected deal in state to prevent UI flicker when closing
    // It will be replaced when a new deal is selected
  }, [navigate, location.pathname, searchParams, setSearchParams, symbolWithExchange]);

  return {
    isOpen,
    selectedDeal,
    currentDealId,
    currentExchangeTicker,
    openModal,
    closeModal,
    setSelectedDeal,
  };
};

export default useInvestmentDetailModal;
